import React from 'react';
import ReactMarkdown from 'react-markdown';
import rehypeSanitize from 'rehype-sanitize';

/**
 * SafeMarkdown — sanitized markdown renderer used by SHS-AI and Support chat.
 *
 * • Runs `rehype-sanitize` to strip any HTML/script/style injections.
 * • Constrains rendered elements to safe inline formatting + lists + code.
 * • Preserves the existing chat bubble typography (text-sm, tight spacing).
 * • No links open in a new tab by default; when the model produces a link
 *   we render it read-only-safe with `rel="noopener noreferrer"`.
 *
 * Educational-only. Never renders raw HTML from the model.
 */
const components = {
  p: ({ children }) => <p className="text-sm whitespace-pre-wrap mb-1 last:mb-0">{children}</p>,
  strong: ({ children }) => <strong className="font-semibold">{children}</strong>,
  em: ({ children }) => <em className="italic">{children}</em>,
  ul: ({ children }) => <ul className="list-disc pl-5 space-y-0.5 text-sm">{children}</ul>,
  ol: ({ children }) => <ol className="list-decimal pl-5 space-y-0.5 text-sm">{children}</ol>,
  li: ({ children }) => <li className="text-sm">{children}</li>,
  code: ({ inline, children }) =>
    inline
      ? <code className="px-1 py-0.5 rounded bg-gray-100 text-[12px] font-mono">{children}</code>
      : <pre className="bg-gray-100 rounded p-2 text-[12px] font-mono overflow-x-auto"><code>{children}</code></pre>,
  a: ({ href, children }) => (
    <a href={href} target="_blank" rel="noopener noreferrer" className="text-purple-700 underline break-words">
      {children}
    </a>
  ),
  h1: ({ children }) => <p className="text-sm font-semibold mb-1">{children}</p>,
  h2: ({ children }) => <p className="text-sm font-semibold mb-1">{children}</p>,
  h3: ({ children }) => <p className="text-sm font-semibold mb-1">{children}</p>,
  blockquote: ({ children }) => <blockquote className="border-l-2 border-gray-300 pl-2 italic text-sm">{children}</blockquote>,
};

const SafeMarkdown = ({ children, className = '' }) => {
  const content = typeof children === 'string' ? children : String(children ?? '');
  return (
    <div className={className} data-testid="safe-markdown">
      <ReactMarkdown rehypePlugins={[rehypeSanitize]} components={components}>
        {content}
      </ReactMarkdown>
    </div>
  );
};

export default SafeMarkdown;
